<template>
	<div :class="['weather-container', { night: !isDay }]" :style="{ background: `linear-gradient(180deg, ${look.gradient[0]} 0%, ${look.gradient[1]} 100%)` }">
		<img v-if="look.lightning" class="lightning-light" :src="lightningLight" alt="" />
		<div class="sky" :style="{ opacity: look.photoOpacity || 1, backgroundImage: `url(${look.photo})` }"></div>
		<img
			v-for="(overlay, index) in look.overlays"
			:key="index"
			class="overlay"
			:src="overlay.src"
			:style="{ mixBlendMode: overlay.blend, opacity: overlay.opacity || 1 }"
			alt=""
		/>

		<div class="effects" aria-hidden="true">
			<template v-if="condition === 'partly-cloudy' || condition === 'cloudy'">
				<div v-for="cloud in clouds" :key="cloud.id" class="cloud" :style="cloud.style"></div>
			</template>
			<div v-if="condition === 'fog'" class="fog"></div>
			<template v-if="condition === 'wind'">
				<div v-for="streak in windStreaks" :key="streak.id" class="streak" :style="streak.style"></div>
			</template>
			<template v-if="condition === 'rain' || condition === 'storm'">
				<div v-for="drop in raindrops" :key="drop.id" class="drop" :style="drop.style"></div>
			</template>
			<div v-if="condition === 'storm'" class="flash"></div>
			<template v-if="condition === 'snow'">
				<div v-for="flake in snowflakes" :key="flake.id" class="flake" :style="flake.style"></div>
			</template>
		</div>

		<p class="label">{{ error ? 'Weather unavailable' : labels[condition] }}</p>
		<div class="icon">
			<div class="icon-box" :style="{ inset: look.icon.inset }">
				<img :src="look.icon.src" alt="" />
			</div>
		</div>
		<div class="title">
			<p class="temperature">{{ temperature === null ? '–' : Math.round(temperature) }}°</p>
			<p class="location">{{ location ? location.name : (error ? 'Check your connection' : 'Locating…') }}</p>
		</div>
	</div>
</template>

<script>
	import axios from 'axios';

	import clearSky from './images/clear-sky.jpg';
	import partlyCloudySky from './images/partly-cloudy-sky.jpg';
	import cloudySky from './images/cloudy-sky.jpg';
	import nightSky from './images/night-sky.jpg';
	import nightCloudySky from './images/night-cloudy-sky.jpg';
	import windLeaves from './images/wind-leaves.webp';
	import rainOnGlass from './images/rain-on-glass.webp';
	import rainOnGlass2 from './images/rain-on-glass-2.webp';
	import stormRain from './images/storm-rain.webp';
	import snowflakes from './images/snowflakes.webp';
	import lightningLight from './images/lightning-light.svg';
	import iconClear from './images/icon-clear.svg';
	import iconClearNight from './images/icon-clear-night.svg';
	import iconPartlyCloudy from './images/icon-partly-cloudy.svg';
	import iconCloudy from './images/icon-cloudy.svg';
	import iconWind from './images/icon-wind.svg';
	import iconFog from './images/icon-fog.svg';
	import iconRain from './images/icon-rain.svg';
	import iconStorm from './images/icon-storm.svg';
	import iconSnow from './images/icon-snow.svg';

	const REFRESH_INTERVAL = 15 * 60 * 1000;
	const LOCATION_CACHE_KEY = 'weatherLocation';
	const LOCATION_CACHE_TIME = 60 * 60 * 1000;
	const FAHRENHEIT_COUNTRIES = ['US', 'LR', 'MM', 'BS', 'BZ', 'KY', 'PW'];
	const CONDITIONS = ['clear', 'partly-cloudy', 'cloudy', 'wind', 'fog', 'rain', 'storm', 'snow'];
	// Wind faster than this (km/h) shows the wind state when nothing is falling
	const WINDY_SPEED = 30;

	// The icon svgs include their shadow, inset places the 72px icon inside them like in Figma
	const icons = {
		clear: { src: iconClear, inset: '-6.94% -12.5% -18.06% -12.5%' },
		clearNight: { src: iconClearNight, inset: '0 -12.5% -18.06% -4.17%' },
		partlyCloudy: { src: iconPartlyCloudy, inset: '0' },
		cloudy: { src: iconCloudy, inset: '0 -12.5% -5.56% -12.5%' },
		wind: { src: iconWind, inset: '0 -8.33% -5.56% -8.33%' },
		fog: { src: iconFog, inset: '-2.78% -8.33% -13.89% -8.33%' },
		rain: { src: iconRain, inset: '-2.78% -8.33% -13.9% -8.33%' },
		storm: { src: iconStorm, inset: '-2.78% -8.33% -22.22% -8.33%' },
		snow: { src: iconSnow, inset: '-2.78% -8.33% -13.89% -8.33%' },
	};

	const rainOverlays = [{ src: rainOnGlass, blend: 'plus-lighter' }];
	const stormOverlays = [
		{ src: stormRain, blend: 'screen' },
		{ src: rainOnGlass, blend: 'plus-lighter' },
		{ src: rainOnGlass2, blend: 'screen' },
	];

	// Layers of each state, from the "Weather" section of the Figma file
	const looks = {
		day: {
			'clear': { gradient: ['#0025A7', '#74BCFF'], photo: clearSky, icon: icons.clear },
			'partly-cloudy': { gradient: ['#001767', '#739DC5'], photo: partlyCloudySky, photoOpacity: 0.9, icon: icons.partlyCloudy },
			'cloudy': { gradient: ['#3D4564', '#7A97B1'], photo: cloudySky, icon: icons.cloudy },
			'wind': { gradient: ['#3D4564', '#7A97B1'], photo: cloudySky, icon: icons.wind, overlays: [{ src: windLeaves, blend: 'screen' }] },
			'fog': { gradient: ['#888EA3', '#A1AAB2'], photo: cloudySky, icon: icons.fog },
			'rain': { gradient: ['#000000', '#586D80'], photo: cloudySky, photoOpacity: 0.7, icon: icons.rain, overlays: rainOverlays },
			'storm': { gradient: ['#000000', '#586D80'], photo: cloudySky, photoOpacity: 0.7, icon: icons.storm, overlays: stormOverlays, lightning: true },
			'snow': { gradient: ['#CCCCCC', '#A1A1A1'], photo: cloudySky, photoOpacity: 0.7, icon: icons.snow, overlays: [{ src: snowflakes, blend: 'plus-lighter' }] },
		},
		night: {
			'clear': { gradient: ['#000000', '#12293F'], photo: nightSky, icon: icons.clearNight },
			'wind': { gradient: ['#000000', '#272F36'], photo: nightCloudySky, icon: icons.wind, overlays: [{ src: windLeaves, blend: 'screen', opacity: 0.3 }] },
			// Not exported from Figma yet (tool limit), built from the night assets above
			'partly-cloudy': { gradient: ['#000000', '#1C2A3A'], photo: nightCloudySky, icon: icons.clearNight },
			'cloudy': { gradient: ['#000000', '#272F36'], photo: nightCloudySky, icon: icons.cloudy },
			'fog': { gradient: ['#1F2529', '#3D4549'], photo: nightCloudySky, icon: icons.fog },
			'rain': { gradient: ['#000000', '#1C2C3B'], photo: nightCloudySky, photoOpacity: 0.7, icon: icons.rain, overlays: rainOverlays },
			'storm': { gradient: ['#000000', '#24303C'], photo: nightCloudySky, photoOpacity: 0.7, icon: icons.storm, overlays: stormOverlays, lightning: true },
			'snow': { gradient: ['#1C2528', '#3A4549'], photo: nightCloudySky, photoOpacity: 0.7, icon: icons.snow, overlays: [{ src: snowflakes, blend: 'plus-lighter' }] },
		},
	};

	// Open-Meteo WMO weather codes
	function conditionFromCode(code, windSpeed) {
		if (code >= 95) return 'storm';
		if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
		if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
		if (code === 45 || code === 48) return 'fog';
		if (windSpeed >= WINDY_SPEED) return 'wind';
		if (code === 3) return 'cloudy';
		if (code === 2) return 'partly-cloudy';
		return 'clear';
	}

	// Positions are random but fixed for the lifetime of the widget
	function particles(count, makeStyle) {
		return Array.from({ length: count }, (_, id) => ({ id, style: makeStyle(Math.random, id) }));
	}

	export default {
		name: 'WeatherComponent',
		data() {
			const query = this.$route.query;
			return {
				labels: {
					'clear': 'Clear',
					'partly-cloudy': 'Partly cloudy',
					'cloudy': 'Cloudy',
					'wind': 'Wind',
					'fog': 'Fog',
					'rain': 'Rain',
					'storm': 'Storm',
					'snow': 'Snow',
				},
				lightningLight,
				// ?condition=rain&night=1 previews a state without real data
				previewCondition: CONDITIONS.includes(query.condition) ? query.condition : null,
				condition: 'clear',
				isDay: !query.night,
				temperature: null,
				location: null,
				error: false,
				interval: null,
				clouds: particles(5, (rnd) => ({
					left: `${rnd() * 100 - 30}%`,
					top: `${rnd() * 45 - 10}%`,
					width: `${180 + rnd() * 160}px`,
					height: `${90 + rnd() * 60}px`,
					animationDuration: `${40 + rnd() * 40}s`,
					animationDelay: `${-rnd() * 40}s`,
				})),
				windStreaks: particles(9, (rnd) => ({
					top: `${8 + rnd() * 60}%`,
					width: `${60 + rnd() * 120}px`,
					animationDuration: `${1.6 + rnd() * 1.6}s`,
					animationDelay: `${-rnd() * 3}s`,
				})),
				raindrops: particles(70, (rnd) => ({
					left: `${rnd() * 110 - 5}%`,
					height: `${14 + rnd() * 18}px`,
					opacity: 0.25 + rnd() * 0.45,
					animationDuration: `${0.5 + rnd() * 0.4}s`,
					animationDelay: `${-rnd() * 2}s`,
				})),
				snowflakes: particles(45, (rnd) => {
					const size = 3 + rnd() * 5;
					return {
						left: `${rnd() * 100}%`,
						width: `${size}px`,
						height: `${size}px`,
						opacity: 0.4 + rnd() * 0.5,
						animationDuration: `${6 + rnd() * 6}s`,
						animationDelay: `${-rnd() * 12}s`,
					};
				}),
			}
		},
		computed: {
			look() {
				const look = looks[this.isDay ? 'day' : 'night'][this.condition];
				return { overlays: [], ...look };
			},
		},
		mounted() {
			if (this.previewCondition) {
				this.condition = this.previewCondition;
				this.temperature = 23;
				this.location = { name: 'Budapest, Hungary' };
				return;
			}
			this.refresh();
			this.interval = setInterval(this.refresh, REFRESH_INTERVAL);
		},
		unmounted() {
			clearInterval(this.interval);
		},
		methods: {
			async refresh() {
				try {
					if (!this.location) {
						this.location = await this.getLocation();
					}
					const unit = this.$route.query.unit === 'f' || (this.$route.query.unit !== 'c' && FAHRENHEIT_COUNTRIES.includes(this.location.countryCode))
						? 'fahrenheit'
						: 'celsius';
					const { data } = await axios.get('https://api.open-meteo.com/v1/forecast', {
						params: {
							latitude: this.location.latitude,
							longitude: this.location.longitude,
							current: 'temperature_2m,weather_code,is_day,wind_speed_10m',
							temperature_unit: unit,
						},
					});
					this.temperature = data.current.temperature_2m;
					this.isDay = data.current.is_day === 1;
					this.condition = conditionFromCode(data.current.weather_code, data.current.wind_speed_10m);
					this.error = false;
				} catch (e) {
					console.error('Failed to load weather', e);
					// Keep showing the last weather if there is one
					this.error = this.temperature === null;
				}
			},
			async getLocation() {
				// Unity can pass the location: /weather?lat=47.5&lon=19.04&city=Budapest, Hungary
				const query = this.$route.query;
				if (query.lat && query.lon) {
					return {
						latitude: parseFloat(query.lat),
						longitude: parseFloat(query.lon),
						name: query.city || '',
						countryCode: query.country || '',
					};
				}

				try {
					const cached = JSON.parse(localStorage.getItem(LOCATION_CACHE_KEY));
					if (cached && Date.now() - cached.savedAt < LOCATION_CACHE_TIME) {
						return cached.location;
					}
				} catch (e) {
					// Ignore a broken cache, the location is looked up again
				}

				const location = await this.locateByIp();
				localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify({ location, savedAt: Date.now() }));
				return location;
			},
			async locateByIp() {
				try {
					const { data } = await axios.get('https://ipapi.co/json/');
					if (data.error) {
						throw new Error(data.reason);
					}
					return {
						latitude: data.latitude,
						longitude: data.longitude,
						name: [data.city, data.country_name].filter(Boolean).join(', '),
						countryCode: data.country_code,
					};
				} catch (e) {
					const { data } = await axios.get('https://ipwho.is/');
					if (!data.success) {
						throw new Error(data.message);
					}
					return {
						latitude: data.latitude,
						longitude: data.longitude,
						name: [data.city, data.country].filter(Boolean).join(', '),
						countryCode: data.country_code,
					};
				}
			},
		},
	}
</script>

<style lang="scss">
	html, body {margin: 0; height: 100%; overflow: hidden}
</style>

<style scoped lang="scss">
	.weather-container {
		position: relative;
		width: 400px;
		height: 400px;
		overflow: hidden;
		background-color: #0C0E11;
		color: #FFFFFF;
		font-family: Inter, sans-serif;

		.lightning-light {
			position: absolute;
			top: calc(32px - 129px * 0.5891);
			right: calc(32px - 187px * 0.4064);
			width: calc(187px * 1.8128);
			height: calc(129px * 2.1782);
		}

		.sky {
			position: absolute;
			top: 0;
			left: 0;
			width: 400px;
			height: 300px;
			background-size: 400px 300px;
			mask-image: url('./images/sky-mask.svg');
			mask-size: 400px 300px;
			-webkit-mask-image: url('./images/sky-mask.svg');
			-webkit-mask-size: 400px 300px;
		}

		.overlay {
			position: absolute;
			top: 0;
			left: 0;
			width: 400px;
			height: 400px;
			object-fit: cover;
			pointer-events: none;
		}

		p {
			margin: 0;
			white-space: nowrap;
		}

		&.night .effects .cloud {
			background: radial-gradient(ellipse at center, #FFFFFF26 0%, #FFFFFF0D 45%, #FFFFFF00 70%);
		}

		.label {
			position: absolute;
			left: 40px;
			top: 57px;
			transform: translateY(-50%);
			font-size: 28px;
			font-weight: 500;
			text-shadow: 0px 4px 6px #00000040;
		}

		.icon {
			position: absolute;
			left: 288px;
			top: 206px;
			width: 72px;
			height: 72px;

			.icon-box {
				position: absolute;

				img {
					display: block;
					width: 100%;
					height: 100%;
				}
			}
		}

		.title {
			position: absolute;
			left: 40px;
			bottom: 40px;
			display: flex;
			flex-direction: column;
			gap: 16px;
			filter: drop-shadow(0px 4px 6px #00000029);

			.temperature {
				font-size: 104px;
				font-weight: 400;
				line-height: 1;
				letter-spacing: -5.2px;
			}

			.location {
				font-size: 28px;
				font-weight: 500;
			}
		}
	}

	.effects {
		position: absolute;
		inset: 0;
		pointer-events: none;
		overflow: hidden;

		.cloud {
			position: absolute;
			border-radius: 50%;
			background: radial-gradient(ellipse at center, #FFFFFF59 0%, #FFFFFF1F 45%, #FFFFFF00 70%);
			filter: blur(10px);
			animation: drift linear infinite;
		}

		.fog {
			position: absolute;
			inset: -20%;
			background: radial-gradient(ellipse at 30% 60%, #FFFFFF59 0%, #FFFFFF00 55%),
				radial-gradient(ellipse at 75% 40%, #FFFFFF4D 0%, #FFFFFF00 50%);
			filter: blur(20px);
			animation: fog 18s ease-in-out infinite alternate;
		}

		.streak {
			position: absolute;
			left: -40%;
			height: 2px;
			border-radius: 2px;
			background: linear-gradient(90deg, #FFFFFF00, #FFFFFF99, #FFFFFF00);
			animation: wind linear infinite;
		}

		.drop {
			position: absolute;
			top: -40px;
			width: 2px;
			border-radius: 2px;
			background: linear-gradient(180deg, #FFFFFF00, #FFFFFFCC);
			transform: rotate(12deg);
			animation: rain linear infinite;
		}

		.flake {
			position: absolute;
			top: -10px;
			border-radius: 50%;
			background-color: #FFFFFF;
			animation: snow linear infinite;
		}

		.flash {
			position: absolute;
			inset: 0;
			background-color: #DCE6FF;
			opacity: 0;
			animation: flash 7s infinite;
		}
	}

	@keyframes drift {
		from { transform: translateX(-60px); }
		50% { transform: translateX(60px); }
		to { transform: translateX(-60px); }
	}

	@keyframes fog {
		from { transform: translateX(-8%); }
		to { transform: translateX(8%); }
	}

	@keyframes wind {
		from { transform: translateX(0); opacity: 0; }
		20% { opacity: 1; }
		80% { opacity: 1; }
		to { transform: translateX(640px); opacity: 0; }
	}

	@keyframes rain {
		from { transform: translate(0, 0) rotate(12deg); }
		to { transform: translate(-100px, 480px) rotate(12deg); }
	}

	@keyframes snow {
		from { transform: translate(0, 0); }
		50% { transform: translate(14px, 210px); }
		to { transform: translate(-6px, 420px); }
	}


	@keyframes flash {
		0%, 88%, 100% { opacity: 0; }
		89% { opacity: 0.25; }
		90% { opacity: 0.05; }
		91% { opacity: 0.18; }
		94% { opacity: 0; }
	}
</style>
